/**
 * LeagueTeamInviteAcceptPage — /league-invite/:token
 * ─────────────────────────────────────────────────────────────────
 * The page a coach lands on after clicking the magic link in their
 * email. Shows the league/season they're being invited to, lists
 * the coach's eligible teams, and lets them confirm the attach.
 *
 * The endpoint requires authentication, so this page sits INSIDE
 * the protected route tree. The api interceptor will redirect to
 * /login (preserving this URL as ?next=) if the user isn't logged
 * in yet. After login they return here and complete the accept.
 *
 * Lifecycle handling:
 *   • Valid pending invite → show the team picker.
 *   • No eligible teams (coach has none) → explain + link to team
 *     creation flow.
 *   • Already-accepted   → success state + link to the league.
 *   • Cancelled / expired → polite error state.
 *
 * After successful accept, redirect to the league's dashboard so
 * the coach immediately sees the league they just joined.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Trophy, Users, CheckCircle2, AlertTriangle, ArrowRight, X, Plus,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';

export default function LeagueTeamInviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Form state for the accept submission.
  const [chosenTeamId, setChosenTeamId] = useState('');
  const [divisionOverride, setDivisionOverride] = useState(null);  // null = use invite default; '' = explicit no division
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/league-team-invites/${token}`)
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
        // Auto-select the first eligible team (most coaches have one).
        const first = (data?.my_teams || []).find(t => !t.already_in_season);
        if (first) setChosenTeamId(first.id);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.error || 'Invite not found';
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  async function accept() {
    if (!chosenTeamId) {
      toast.error('Pick a team to attach');
      return;
    }
    setSubmitting(true);
    try {
      const body = { team_id: chosenTeamId };
      if (divisionOverride !== null) body.division_id = divisionOverride;
      const { data: res } = await api.post(`/league-team-invites/${token}/accept`, body);
      toast.success('Welcome to the league!');
      if (res?.league_id) navigate(`/league/${res.league_id}/dashboard`);
      else navigate('/leagues');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not accept invite');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 540 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading invite…</div>
      </div>
    );
  }
  if (error) return <InviteErrorCard message={error} />;
  if (!data?.invite) return <InviteErrorCard message="Invite not found" />;

  const inv = data.invite;
  const state = inv.state;

  if (state === 'cancelled') return <InviteErrorCard
    icon={X} title="Invite cancelled"
    message={`The manager cancelled this invite. Contact ${inv.league_name} if you think this is a mistake.`} />;

  if (state === 'expired') return <InviteErrorCard
    icon={AlertTriangle} title="Invite expired"
    message={`This invite has expired. Ask ${inv.league_name} to send a new one.`} />;

  if (state === 'accepted') return (
    <div className="animate-fade-in" style={{ maxWidth: 540 }}>
      <div className="card" style={{ textAlign: 'center', padding: '36px 24px' }}>
        <CheckCircle2 size={48} color="var(--goal)" style={{ margin: '0 auto 16px' }} />
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Already accepted</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
          You've already joined {inv.league_name} for {inv.season_name}.
        </div>
        <Link to={`/league/${inv.league_id}/dashboard`} className="btn-ice"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Open league
          <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );

  // Pending. Render the accept form.
  const myTeams = data.my_teams || [];
  const eligible = myTeams.filter(t => !t.already_in_season);
  const ineligible = myTeams.filter(t =>  t.already_in_season);

  return (
    <div className="animate-fade-in" style={{ maxWidth: 540 }}>
      {/* Header card */}
      <div className="card" style={{ marginBottom: 16, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'var(--ice-a10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Trophy size={24} color="var(--ice)" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 1 }}>
              You're invited
            </div>
            <h1 className="section-title text-xl" style={{ marginTop: 2 }}>{inv.league_name}</h1>
          </div>
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 6 }}>
          Season: <strong style={{ color: 'var(--text)' }}>{inv.season_name}</strong>
          {' '}({inv.season_start} → {inv.season_end})
        </div>
        {inv.division_name && (
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            Suggested division: <strong style={{ color: 'var(--text)' }}>{inv.division_name}</strong>
          </div>
        )}
        {inv.message && (
          <div style={{
            marginTop: 14, padding: 12,
            background: 'var(--surface-2)', borderRadius: 8,
            borderLeft: '3px solid var(--ice)',
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 4 }}>Personal note</div>
            <div style={{ fontSize: 13, fontStyle: 'italic' }}>"{inv.message}"</div>
          </div>
        )}
      </div>

      {/* Team picker */}
      {eligible.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '28px 20px' }}>
          <Users size={36} color="var(--text-2)" style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          {myTeams.length === 0 ? (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>You don't coach any teams yet</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>
                Create a team first, then come back to this invite link to attach it.
              </div>
              <Link to="/dashboard" className="btn-ice" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Plus size={13} />
                Go to my dashboard
              </Link>
            </>
          ) : (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>All your teams are already in this season</div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                Nothing to attach. If you need a different team in this season, create one first.
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Pick a team to attach</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {eligible.map(t => (
              <TeamRadio key={t.id} team={t} selected={chosenTeamId === t.id}
                onSelect={() => setChosenTeamId(t.id)} />
            ))}
          </div>
          {ineligible.length > 0 && (
            <details style={{ marginTop: 14 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-2)' }}>
                {ineligible.length} team{ineligible.length === 1 ? '' : 's'} already in this season
              </summary>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ineligible.map(t => (
                  <div key={t.id} style={{
                    padding: 8, borderRadius: 6,
                    background: 'var(--surface-2)',
                    fontSize: 12, color: 'var(--text-2)',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <CheckCircle2 size={12} color="var(--goal)" />
                    {t.name}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Action footer */}
      {eligible.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Link to="/leagues" className="btn-ghost">Not now</Link>
          <button
            type="button"
            className="btn-ice"
            onClick={accept}
            disabled={submitting || !chosenTeamId}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {submitting ? 'Joining…' : (
              <>
                Accept &amp; join
                <ArrowRight size={13} />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function TeamRadio({ team, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: 12, borderRadius: 10,
        background: selected ? 'var(--ice-a10)' : 'var(--surface-2)',
        border: `1px solid ${selected ? 'var(--ice-a40)' : 'var(--border)'}`,
        color: 'var(--text)', cursor: 'pointer', textAlign: 'left',
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: '50%',
        background: selected ? 'var(--ice)' : 'transparent',
        border: `1.5px solid ${selected ? 'var(--ice)' : 'var(--text-2)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {selected && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--btn-accent-text)' }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{team.name}</div>
      </div>
    </button>
  );
}

function InviteErrorCard({ icon: Icon = AlertTriangle, title = 'Invite unavailable', message }) {
  return (
    <div className="animate-fade-in" style={{ maxWidth: 540 }}>
      <div className="card" style={{ textAlign: 'center', padding: '36px 24px' }}>
        <Icon size={48} color="var(--puck)" style={{ margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>{message}</div>
        <Link to="/leagues" className="btn-ghost">Back to leagues</Link>
      </div>
    </div>
  );
}
